import type { ProviderUsageDetails, UsageMetrics } from "@/types/usage";
import { countTokensForText, selectEncoding } from "@/hooks/use-token-count";

function normalizeTextArray(values: Array<string | null | undefined> | undefined): string[] {
  if (!values || values.length === 0) {
    return [];
  }

  return values
    .map((value) => (typeof value === "string" ? value.trim() : ""))
    .filter((value) => value.length > 0);
}

async function sumTokenCounts(texts: string[], encoding: string): Promise<number> {
  if (texts.length === 0) {
    return 0;
  }

  const counts = await Promise.all(
    texts.map(async (text) => {
      try {
        const result = await countTokensForText(text, encoding);
        return Number.isFinite(result.count) ? Math.max(0, result.count) : 0;
      } catch {
        return Math.max(0, Math.ceil(text.length / 4));
      }
    })
  );

  return counts.reduce((sum, value) => sum + (Number.isFinite(value) ? value : 0), 0);
}

function addMetric(metrics: Record<string, number>, name: string, value: unknown) {
  const normalizedName = String(name || "").trim();
  if (!normalizedName) {
    return;
  }
  const numericValue = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numericValue) || numericValue < 0) {
    return;
  }
  metrics[normalizedName] = (metrics[normalizedName] ?? 0) + numericValue;
}

function usageDetailsFromMetrics(metrics: Record<string, number>): ProviderUsageDetails | undefined {
  const keys = Object.keys(metrics);
  if (keys.length === 0) {
    return undefined;
  }
  return { metrics };
}

export function parseOpenAIUsage(payload: any): ProviderUsageDetails | undefined {
  const usage = payload?.usage;
  if (!usage || typeof usage !== "object") {
    return undefined;
  }

  const metrics: Record<string, number> = {};

  addMetric(metrics, "promptTokens", (usage as any).prompt_tokens ?? (usage as any).input_tokens);
  addMetric(metrics, "completionTokens", (usage as any).completion_tokens ?? (usage as any).output_tokens);
  addMetric(metrics, "totalTokens", (usage as any).total_tokens);
  addMetric(metrics, "inputTokens", (usage as any).input_tokens);
  addMetric(metrics, "outputTokens", (usage as any).output_tokens);
  addMetric(metrics, "audioTokens", (usage as any).audio_tokens);

  const promptDetails = (usage as any).prompt_tokens_details;
  if (promptDetails && typeof promptDetails === "object") {
    addMetric(metrics, "cachedTokens", (promptDetails as any).cached_tokens);
    addMetric(metrics, "audioTokens", (promptDetails as any).audio_tokens);
  }

  const completionDetails = (usage as any).completion_tokens_details;
  if (completionDetails && typeof completionDetails === "object") {
    addMetric(metrics, "reasoningTokens", (completionDetails as any).reasoning_tokens);
  }

  // Capture any other numeric fields without overriding known ones
  Object.entries(usage as Record<string, unknown>).forEach(([key, value]) => {
    if (
      [
        "prompt_tokens",
        "completion_tokens",
        "total_tokens",
        "input_tokens",
        "output_tokens",
        "audio_tokens",
        "prompt_tokens_details",
        "completion_tokens_details",
      ].includes(key)
    ) {
      return;
    }
    addMetric(metrics, key, value);
  });

  return usageDetailsFromMetrics(metrics);
}

export function parseGeminiUsage(payload: any): ProviderUsageDetails | undefined {
  const usage = payload?.usageMetadata;
  if (!usage || typeof usage !== "object") {
    return undefined;
  }

  const metrics: Record<string, number> = {};

  addMetric(metrics, "promptTokens", (usage as any).promptTokenCount ?? (usage as any).prompt_tokens);
  addMetric(metrics, "completionTokens", (usage as any).candidatesTokenCount ?? (usage as any).candidates_tokens);
  addMetric(metrics, "totalTokens", (usage as any).totalTokenCount ?? (usage as any).total_tokens);
  addMetric(metrics, "cachedTokens", (usage as any).cachedContentTokenCount);
  addMetric(metrics, "inputTokens", (usage as any).inputTokenCount);
  addMetric(metrics, "outputTokens", (usage as any).outputTokenCount);

  Object.entries(usage as Record<string, unknown>).forEach(([key, value]) => {
    if (
      [
        "promptTokenCount",
        "prompt_tokens",
        "candidatesTokenCount",
        "candidates_tokens",
        "totalTokenCount",
        "total_tokens",
        "cachedContentTokenCount",
        "inputTokenCount",
        "outputTokenCount",
      ].includes(key)
    ) {
      return;
    }
    addMetric(metrics, key, value);
  });

  return usageDetailsFromMetrics(metrics);
}

interface EstimateUsageOptions {
  provider?: string | null;
  model?: string | null;
  promptTexts?: Array<string | null | undefined>;
  completionTexts?: Array<string | null | undefined>;
}

export async function estimateUsageMetrics(options: EstimateUsageOptions): Promise<UsageMetrics | undefined> {
  const encoding = selectEncoding(options.provider ?? undefined, options.model ?? undefined);
  const promptTexts = normalizeTextArray(options.promptTexts);
  const completionTexts = normalizeTextArray(options.completionTexts);

  if (promptTexts.length === 0 && completionTexts.length === 0) {
    return undefined;
  }

  const [promptTokens, completionTokens] = await Promise.all([
    sumTokenCounts(promptTexts, encoding),
    sumTokenCounts(completionTexts, encoding),
  ]);

  const metrics: UsageMetrics = {};

  if (promptTokens > 0) {
    metrics.promptTokens = promptTokens;
  }

  if (completionTokens > 0) {
    metrics.completionTokens = completionTokens;
  }

  const totalTokens = promptTokens + completionTokens;
  if (totalTokens > 0) {
    metrics.totalTokens = totalTokens;
  }

  return Object.keys(metrics).length > 0 ? metrics : undefined;
}
